import type { UserProfile } from "@/app/profile/types";
import { createBrowserClient } from "../supabase/config";

/**
 * Fetches the current user's profile from Supabase directly.
 */
export async function getProfile(): Promise<UserProfile | null> {
  const supabase = createBrowserClient();
  // Get session from client-side storage
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  if (sessionError || !session) {
    throw new Error(sessionError?.message || "Not authenticated");
  }
  const user = session.user;
  // Fetch the profile row for the authenticated user
  const { data: profile, error } = await supabase
    .from("user_profiles")
    .select("*")
    .eq("user_id", user.id)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null; // no rows
    throw new Error(error?.message || "Failed to fetch profile");
  }
  return profile;
}

/**
 * Updates the user's profile with the given partial data.
 */
export async function updateProfile(
  updates: Partial<UserProfile>
): Promise<UserProfile> {
  const supabase = createBrowserClient();
  // Ensure we know the authenticated user's ID
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  if (sessionError || !session) {
    throw new Error(sessionError?.message || "Not authenticated");
  }
  const user_id = session.user.id;

  // Merge user_id and timestamp into the upsert data
  const updateData: Partial<UserProfile> = {
    user_id,
    updated_at: new Date().toISOString(),
    ...updates,
  };

  const { data: profile, error } = await supabase
    .from("user_profiles")
    .upsert(updateData, { onConflict: "user_id" })
    .select()
    .single();
  if (error || !profile) {
    throw new Error(error?.message || "Failed to update profile");
  }
  return profile;
}
